// src/pages/task/views/BoardView.tsx
// Kanban board view with @dnd-kit drag & drop support.
// Columns are built dynamically from custom status definitions or fall back to defaults.

import { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { observer } from '@react';
import { useI18n } from '@react/hooks';
import { EntityAvatar } from '@/components/EntityAvatar';
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  useSensor,
  useSensors,
  closestCorners,
  useDroppable,
  type DragStartEvent,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import type { Task, TaskStatusDefinition } from '@core/types/task';
import type { DensityMode } from '@/features/workspace/types';

import {
  getEntityId,
  getTaskStatus,
  getTaskPriority,
  getTaskAssignee,
  getTaskDueDate,
  getTaskType,
  getTaskTypeInfo,
  getPriorityColor,
  getPriorityIconPath,
  getDueDateUrgency,
  getDisplayName,
  formatDate,
  buildBoardColumns,
  type BoardColumn,
} from '@/features/task/logic';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface BoardViewProps {
  tasks: Task[];
  statusDefinitions?: TaskStatusDefinition[];
  groupBy: string | null;
  density: DensityMode;
  onTaskClick: (id: string) => void;
  /** `columnId` is a base status enum or the slug of a custom status definition. */
  onTaskMove?: (taskId: string, columnId: string) => void;
  /** Called when a drag begins so the page can resolve allowed transitions. */
  onDragStartTask?: (taskId: string) => void;
  /**
   * Column ids the dragged task may move to. `null` means unconstrained
   * (no workflow configured, or the transitions could not be resolved).
   */
  allowedColumnIds?: string[] | null;
  /** Called when a drop lands on a column the workflow forbids. */
  onBlockedMove?: (taskId: string, columnId: string) => void;
  /** Create a task directly in a column. */
  onQuickAdd?: (columnId: string, title: string) => void | Promise<void>;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const BoardView = observer(({
  tasks,
  statusDefinitions,
  groupBy: _groupBy,
  density,
  onTaskClick,
  onTaskMove,
  onDragStartTask,
  allowedColumnIds = null,
  onBlockedMove,
  onQuickAdd,
}: BoardViewProps) => {
  const { t, locale } = useI18n();
  const [activeId, setActiveId] = useState<string | null>(null);
  const [quickAddColumn, setQuickAddColumn] = useState<string | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
  );

  // Build columns from custom definitions or defaults
  const columns = useMemo(
    () => buildBoardColumns(statusDefinitions || [], tasks),
    [statusDefinitions, tasks],
  );

  const columnIds = useMemo(() => columns.map(c => c.id), [columns]);

  const activeTask = useMemo(
    () => activeId ? tasks.find(t => getEntityId(t) === activeId) : null,
    [activeId, tasks],
  );

  /** The column the dragged card started in — always a legal drop target. */
  const sourceColumnId = useMemo(
    () => (activeId
      ? columns.find(c => c.tasks.some(ct => getEntityId(ct) === activeId))?.id ?? null
      : null),
    [activeId, columns],
  );

  /**
   * A column accepts a drop when there is no transition constraint at all,
   * when it is where the card came from, or when the workflow lists either the
   * column slug or its base category.
   */
  const isColumnAllowed = useCallback(
    (columnId: string) => {
      if (!allowedColumnIds) return true;
      if (columnId === sourceColumnId) return true;
      if (allowedColumnIds.includes(columnId)) return true;
      const definition = (statusDefinitions || []).find(def => def.slug === columnId);
      return !!definition?.category && allowedColumnIds.includes(definition.category);
    },
    [allowedColumnIds, sourceColumnId, statusDefinitions],
  );

  const handleDragStart = useCallback((event: DragStartEvent) => {
    const taskId = String(event.active.id);
    setActiveId(taskId);
    onDragStartTask?.(taskId);
  }, [onDragStartTask]);

  const handleDragEnd = useCallback((event: DragEndEvent) => {
    setActiveId(null);
    const { active, over } = event;
    if (!over || !onTaskMove) return;

    const taskId = String(active.id);
    let targetColumnId = String(over.id);

    // If dropped on a task, find that task's column
    if (!columnIds.includes(targetColumnId)) {
      const overTask = tasks.find(t => getEntityId(t) === targetColumnId);
      if (overTask) {
        // Find which column this task belongs to
        const col = columns.find(c => c.tasks.some(ct => getEntityId(ct) === targetColumnId));
        targetColumnId = col?.id || columnIds[0];
      }
    }

    // Only move if column actually changed
    const currentTask = tasks.find(t => getEntityId(t) === taskId);
    if (currentTask) {
      const currentCol = columns.find(c => c.tasks.some(ct => getEntityId(ct) === taskId));
      if (currentCol && currentCol.id !== targetColumnId) {
        if (!isColumnAllowed(targetColumnId)) {
          onBlockedMove?.(taskId, targetColumnId);
          return;
        }
        onTaskMove(taskId, targetColumnId);
      }
    }
  }, [tasks, columns, columnIds, onTaskMove, isColumnAllowed, onBlockedMove]);

  const handleDragCancel = useCallback(() => {
    setActiveId(null);
  }, []);

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCorners}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragCancel={handleDragCancel}
    >
      <div className="flex gap-4 overflow-x-auto pb-4 -mx-2 px-2">
        {columns.map(col => {
          const label = col.labelKey ? t(col.labelKey) : col.label;
          const taskIds = col.tasks.map(t => getEntityId(t));

          return (
            <DroppableColumn
              key={col.id}
              column={col}
              label={label}
              blocked={!!activeId && !isColumnAllowed(col.id)}
            >
              <SortableContext
                items={taskIds}
                strategy={verticalListSortingStrategy}
              >
                <div className="space-y-2 min-h-[120px]">
                  {col.tasks.length === 0 ? (
                    <div className="flex items-center justify-center h-24 text-xs text-muted-foreground">
                      {t('task.noItems')}
                    </div>
                  ) : (
                    col.tasks.map(task => (
                      <DraggableTaskCard
                        key={getEntityId(task)}
                        task={task}
                        density={density}
                        onTaskClick={onTaskClick}
                        locale={locale}
                        t={t}
                      />
                    ))
                  )}
                </div>
              </SortableContext>

              {/* Quick add */}
              {onQuickAdd && (
                <div className="mt-2 pt-2 border-t border-border/50">
                  {quickAddColumn === col.id ? (
                    <QuickAddForm
                      columnId={col.id}
                      onSubmit={onQuickAdd}
                      onDismiss={() => setQuickAddColumn(null)}
                    />
                  ) : (
                    <button
                      type="button"
                      onClick={() => setQuickAddColumn(col.id)}
                      className="flex items-center gap-1 w-full text-xs text-muted-foreground hover:text-foreground transition-colors py-1"
                    >
                      <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
                      </svg>
                      {t('task.quickAdd')}
                    </button>
                  )}
                </div>
              )}
            </DroppableColumn>
          );
        })}
      </div>

      {/* Drag overlay */}
      <DragOverlay>
        {activeTask && (
          <div className="rounded-lg border border-primary/50 bg-card p-3 shadow-lg opacity-90 w-64">
            <TaskCardContent task={activeTask} density={density} locale={locale} t={t} />
          </div>
        )}
      </DragOverlay>
    </DndContext>
  );
});

// ---------------------------------------------------------------------------
// Quick add
// ---------------------------------------------------------------------------

interface QuickAddFormProps {
  columnId: string;
  onSubmit: (columnId: string, title: string) => void | Promise<void>;
  onDismiss: () => void;
}

function QuickAddForm({ columnId, onSubmit, onDismiss }: QuickAddFormProps) {
  const { t } = useI18n();
  const [value, setValue] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // Focus on mount rather than via autoFocus, which fights screen readers.
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const submit = async () => {
    const title = value.trim();
    if (!title || isSaving) return;
    setIsSaving(true);
    try {
      await onSubmit(columnId, title);
      setValue('');
      inputRef.current?.focus();
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <form
      onSubmit={e => {
        e.preventDefault();
        void submit();
      }}
      className="flex gap-1"
    >
      <input
        ref={inputRef}
        type="text"
        value={value}
        disabled={isSaving}
        onChange={e => setValue(e.target.value)}
        placeholder={t('task.quickAdd')}
        aria-label={t('task.quickAdd')}
        className="flex-1 rounded border border-border bg-background px-2 py-1 text-xs text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary disabled:opacity-60"
        onKeyDown={e => {
          if (e.key === 'Escape') {
            e.preventDefault();
            onDismiss();
          }
        }}
        onBlur={() => {
          if (!value.trim() && !isSaving) onDismiss();
        }}
      />
      <button
        type="submit"
        disabled={!value.trim() || isSaving}
        className="rounded bg-primary px-2 py-1 text-xs font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50 transition-colors"
      >
        {isSaving ? t('common.loading') : t('common.add')}
      </button>
    </form>
  );
}

// ---------------------------------------------------------------------------
// Droppable column
// ---------------------------------------------------------------------------

interface DroppableColumnProps {
  column: BoardColumn;
  label: string;
  /** The task being dragged cannot transition into this column. */
  blocked?: boolean;
  children: React.ReactNode;
}

function DroppableColumn({ column, label, blocked = false, children }: DroppableColumnProps) {
  const { setNodeRef, isOver } = useDroppable({ id: column.id, disabled: blocked });

  return (
    <div
      ref={setNodeRef}
      className={`flex-shrink-0 w-72 flex flex-col transition-opacity ${
        isOver && !blocked ? 'ring-2 ring-primary/40 rounded-lg' : ''
      } ${blocked ? 'opacity-40' : ''}`}
      data-column-id={column.id}
      aria-disabled={blocked || undefined}
    >
      {/* Column header */}
      <div className="flex items-center gap-2 mb-3 px-1">
        {column.accentColor ? (
          <span className={`inline-block h-2.5 w-2.5 rounded-full ${column.accentColor}`} />
        ) : (
          <span
            className="inline-block h-2.5 w-2.5 rounded-full"
            style={{ backgroundColor: column.accentHex }}
          />
        )}
        <h3 className="text-sm font-semibold text-foreground">{label}</h3>
        <span className="ml-auto inline-flex items-center justify-center rounded-full bg-muted px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground">
          {column.tasks.length}
        </span>
      </div>

      {/* Column body. Height comes from the column, which comes from the pane —
          the window's height says nothing about how tall a pane is. */}
      <div className="flex-1 min-h-0 overflow-y-auto rounded-lg bg-muted/30 p-2">
        {children}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Draggable task card
// ---------------------------------------------------------------------------

interface DraggableTaskCardProps {
  task: Task;
  density: DensityMode;
  onTaskClick: (id: string) => void;
  locale: string;
  t: (key: string) => string;
}

function DraggableTaskCard({ task, density, onTaskClick, locale, t }: DraggableTaskCardProps) {
  const id = getEntityId(task);
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className={`rounded-lg border border-border bg-card shadow-sm transition-shadow hover:shadow-md cursor-grab active:cursor-grabbing ${
        isDragging ? 'opacity-50' : ''
      } ${density === 'compact' ? 'p-2' : 'p-3'}`}
      onClick={() => onTaskClick(id)}
      role="button"
      tabIndex={0}
      onKeyDown={e => { if (e.key === 'Enter') onTaskClick(id); }}
      aria-label={(task as any).title || ''}
    >
      <TaskCardContent task={task} density={density} locale={locale} t={t} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Card content (shared between card and drag overlay)
// ---------------------------------------------------------------------------

interface TaskCardContentProps {
  task: Task;
  density: DensityMode;
  locale: string;
  t: (key: string) => string;
}

function TaskCardContent({ task, density, locale, t }: TaskCardContentProps) {
  const taskType = getTaskType(task);
  const priority = getTaskPriority(task);
  const dueDate = getTaskDueDate(task);
  const assignee = getTaskAssignee(task);
  const status = getTaskStatus(task);

  const typeInfo = getTaskTypeInfo(taskType);
  const priorityColor = getPriorityColor(priority);
  const priorityIconPath = getPriorityIconPath(priority);
  const dueUrgency = getDueDateUrgency(dueDate, status);
  const assigneeName = assignee ? getDisplayName(assignee) : null;

  if (density === 'compact') {
    return (
      <div className="flex items-center gap-2">
        {typeInfo && (
          <svg className="h-3.5 w-3.5 flex-shrink-0 text-muted-foreground" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d={typeInfo.iconPath} />
          </svg>
        )}
        <span className="text-xs font-medium text-foreground truncate flex-1">
          {(task as any).title || t('common.untitled')}
        </span>
        {priorityIconPath && (
          <svg className={`h-3.5 w-3.5 flex-shrink-0 ${priorityColor}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d={priorityIconPath} />
          </svg>
        )}
        {assigneeName && (
          <EntityAvatar name={assigneeName} image={assignee?.avatar} userId={assignee?.entity?.id} size="xs" className="h-5 w-5 text-[8px]" />
        )}
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-start gap-2 mb-2">
        {typeInfo && (
          <svg className="h-4 w-4 mt-0.5 flex-shrink-0 text-muted-foreground" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d={typeInfo.iconPath} />
          </svg>
        )}
        <span className="text-sm font-medium text-foreground line-clamp-2 flex-1">
          {(task as any).title || t('common.untitled')}
        </span>
      </div>

      {(task as any).description && (
        <p className="text-xs text-muted-foreground line-clamp-1 mb-2">
          {(task as any).description}
        </p>
      )}

      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          {priorityIconPath && (
            <svg className={`h-3.5 w-3.5 ${priorityColor}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d={priorityIconPath} />
            </svg>
          )}
          {dueDate && (
            <span className={`text-[10px] tabular-nums whitespace-nowrap ${
              dueUrgency === 'overdue' ? 'text-destructive font-medium' :
              dueUrgency === 'soon' ? 'text-warning font-medium' :
              'text-muted-foreground'
            }`}>
              {formatDate(dueDate, locale)}
            </span>
          )}
        </div>

        {/* Unassigned cards stay quiet; the dashed placeholder read as a
            broken avatar on every card. */}
        {assigneeName && (
          <EntityAvatar name={assigneeName} image={assignee?.avatar} userId={assignee?.entity?.id} size="xs" />
        )}
      </div>
    </div>
  );
}
